import { memo, useMemo, useEffect } from 'react';
import { View } from 'react-native';
import {
  BoxProps,
  Image,
  LobbyWidget,
  useBreakpointProp,
} from '@bonanza-social/moonlight';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { useTranslation } from '@bonanza-social/i18n';
import { useShopSales } from '../hooks';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

function ShopLobbyWidget() {
  const { shopSales } = useShopSales();

  const hasSale = useMemo(
    () => shopSales && shopSales?.length > 0,
    [shopSales],
  );

  const badgeProps = useBreakpointProp<BoxProps>({
    mobile: {
      top: 'space-200',
      right: 'space-050',
    },
    tablet: {
      top: 'space-100',
      right: '-space-200',
    },
    desktop: {
      top: 'space-100',
      right: '-space-200',
    },
  });

  const pulse = useSharedValue(1);

  const Component = useMemo(() => (hasSale ? Animated.View : View), [hasSale]);

  useEffect(() => {
    const createKeyframeAnimation = () => {
      pulse.value = withTiming(1.3, {
        duration: 200,
        easing: Easing.inOut(Easing.ease),
      });
      setTimeout(() => {
        pulse.value = withTiming(1, {
          duration: 200,
          easing: Easing.inOut(Easing.ease),
        });
      }, 1500);
    };

    const startAnimation = () => {
      createKeyframeAnimation();
      const interval = setInterval(createKeyframeAnimation, 2000);
      return () => clearInterval(interval);
    };

    const cleanup = startAnimation();
    return cleanup;
  }, [pulse]);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: pulse.value,
        },
      ],
    };
  });

  const [t] = useTranslation('shop');
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();

  const handleClick = () => {
    navigation.navigate('Shop');
  };

  return (
    <LobbyWidget
      image="shop.lobbyWidgetIcon"
      label={t('widgetLabel')}
      onClick={handleClick}
      badgeBoxProps={badgeProps}
      badgeChildren={
        hasSale ? (
          <Component style={animatedStyles}>
            <Image
              name="shop.saleBadge"
              width={{
                mobile: 32,
                tablet: 48,
                desktop: 48,
              }}
              height={{
                mobile: 32,
                tablet: 48,
                desktop: 48,
              }}
            />
          </Component>
        ) : undefined
      }
      hasUnread={hasSale}
    />
  );
}

export default memo(ShopLobbyWidget);
